import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileDown, RefreshCcw, Trophy, UserPlus } from "lucide-react";
import { toast } from "sonner";

const downloadBonusPdf = async (companyId, b) => {
  try {
    const res = await apiClient.get(`/companies/${companyId}/bonus-summaries/${b.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Bonusabrechnung_${b.period}_${(b.employee_name || b.gf_name || "GF").replace(/\s+/g, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Bonusabrechnung konnte nicht erzeugt werden");
  }
};

const fmtChf = (v) => (v === null || v === undefined ? "—"
  : new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(Number(v)));
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const fmtPeriod = (p) => (p ? `${MONTHS[Number(p.slice(5, 7)) - 1] || p.slice(5)} ${p.slice(0, 4)}` : "—");
const STATUS = {
  unmatched: { label: "nicht zugeordnet", cls: "bg-red-100 text-red-800", title: "Kein Mitarbeiter mit dieser AHV-Nr./E-Mail gefunden — Mitarbeiter anlegen, dann wird der Bonus automatisch übernommen" },
  pending: { label: "wartet", cls: "bg-slate-100 text-slate-700", title: "Wartet (z.B. auf Stunden des Stundenlöhners) oder Betrag 0" },
  draft: { label: "im Entwurf", cls: "bg-amber-100 text-amber-800", title: "Bonus ist im Lohnlauf-Entwurf enthalten — unten prüfen und buchen" },
  posted: { label: "gebucht", cls: "bg-emerald-100 text-emerald-800", title: "Lohnlauf mit diesem Bonus ist gebucht" },
};

export function BonusSummariesCard({ companyId, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/bonus-summaries`);
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const reprocess = async () => {
    setReprocessing(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/worktime-summaries/reprocess`);
      const b = r.data?.bonuses || {};
      const runs = (b.payroll || []).filter((p) => p.payroll_run_id).length;
      toast.success(`${b.matched || 0} Boni zugeordnet · ${runs} in Lohnläufe übernommen`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nachverarbeitung fehlgeschlagen");
    } finally {
      setReprocessing(false);
    }
  };

  const openCount = items.filter((b) => b.payroll_status === "unmatched" || b.payroll_status === "pending").length;
  if (!loading && items.length === 0) return null;

  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="bonus-summaries-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-slate-800">GF-Boni aus der StrongFit-App</h3>
          <span className="text-xs text-slate-400">Standort-Bonus · persönlicher Anteil fliesst in den Lohnlauf</span>
        </div>
        <div className="flex items-center gap-2">
          {openCount > 0 && (
            <Button variant="outline" size="sm" onClick={reprocess} disabled={reprocessing} title="Offene Boni erneut zuordnen und in Lohnlauf-Entwürfe übernehmen" data-testid="bonus-reprocess-button">
              <UserPlus className={`h-3.5 w-3.5 mr-1 ${reprocessing ? "animate-pulse" : ""}`} />
              Nachverarbeiten ({openCount})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} title="Bonus-Übersicht neu laden" data-testid="bonus-refresh-button">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
      </div>
      <Table data-testid="bonus-summaries-table">
        <TableHeader>
          <TableRow>
            <TableHead>Monat</TableHead>
            <TableHead>GF</TableHead>
            <TableHead>Mitarbeiter (ERP)</TableHead>
            <TableHead>Studio</TableHead>
            <TableHead className="text-right">Umsatz</TableHead>
            <TableHead className="text-right">Bonus-%</TableHead>
            <TableHead className="text-right">Studio-Bonus</TableHead>
            <TableHead className="text-right">Anteil</TableHead>
            <TableHead className="text-right">Pers. Bonus</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">PDF</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((b) => {
            const st = STATUS[b.payroll_status] || STATUS.pending;
            return (
              <TableRow key={b.id} data-testid={`bonus-row-${b.external_id}`}>
                <TableCell className="text-sm">{fmtPeriod(b.period)}</TableCell>
                <TableCell className="text-sm">
                  <div>{b.gf_name || "—"}</div>
                  <div className="text-[11px] text-slate-400">{b.gf_email || ""}</div>
                </TableCell>
                <TableCell className={`text-sm ${b.employee_name ? "" : "text-red-600"}`}>{b.employee_name || "—"}</TableCell>
                <TableCell className="text-sm">{b.studio_name || "—"}</TableCell>
                <TableCell className="text-right text-sm">{fmtChf(b.revenue)}</TableCell>
                <TableCell className="text-right text-sm">{b.bonus_percent != null ? `${Number(b.bonus_percent).toFixed(1)}%` : "—"}</TableCell>
                <TableCell className="text-right text-sm">{fmtChf(b.studio_bonus_chf)}</TableCell>
                <TableCell className="text-right text-sm">{b.share_percent != null ? `${Number(b.share_percent).toFixed(0)}%` : "—"}</TableCell>
                <TableCell className="text-right text-sm font-semibold">{fmtChf(b.personal_bonus_chf)}</TableCell>
                <TableCell>
                  <Badge className={st.cls} title={st.title} data-testid={`bonus-status-${b.external_id}`}>{st.label}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => downloadBonusPdf(companyId, b)} title="Bonusabrechnung als PDF herunterladen" data-testid={`bonus-pdf-${b.external_id}`}>
                    <FileDown className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
